// gti-chms-frontend/src/context/AuthContext.tsx
import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import type { ReactNode } from 'react';
import api from '../api/axiosConfig'; // Import your configured Axios instance, typically named 'api' or 'instance'

// Define the shape of a logged-in user
export interface User {
    _id: string;
    churchId: string; // The ID of the church the user belongs to (this comes from backend on login)
    firstName: string;
    lastName: string;
    email: string;
    role: 'member' | 'admin' | 'super-admin';
    isActive: boolean;
    token: string; // JWT token
    phoneNumber?: any;
    address?: any;
}

// Define the shape of the AuthContext
interface AuthContextType {
    user: User | null;
    login: (email: string, password: string) => Promise<void>;
    logout: () => void;
    authLoading?: any
}

// Create the context with a default null value
const AuthContext = createContext<AuthContextType | null>(null);

// AuthProvider component
interface AuthProviderProps {
    children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);

    // Load user from localStorage on initial load
    useEffect(() => {
        try {
            const storedUser = localStorage.getItem('user');
            if (storedUser) {
                const parsedUser: User = JSON.parse(storedUser);
                setUser(parsedUser);
                // Axios interceptor will automatically pick up the token from localStorage
                // So, no need to manually set axios.defaults.headers.common['Authorization'] here
            }
        } catch (error) {
            console.error("Failed to load user from localStorage:", error);
            localStorage.removeItem('user'); // Clear corrupted storage
        }
    }, []);

    // Login function
    const login = useCallback(async (email: string, password: string) => {
        try {
            // Use the configured 'api' instance
            // The X-Church-Identifier and Content-Type headers are handled by the interceptor.
            const response = await api.post('/auth/login', { email, password });

            const userData: User = {
                _id: response.data._id,
                churchId: response.data.churchId,
                firstName: response.data.firstName,
                lastName: response.data.lastName,
                email: response.data.email,
                role: response.data.role,
                isActive: response.data.isActive,
                token: response.data.token,
            };

            setUser(userData);
            localStorage.setItem('user', JSON.stringify(userData));
            // Axios interceptor will automatically pick up the token from localStorage for subsequent requests.

        } catch (error: any) {
            console.error('Login failed:', error.response?.data?.message || error.message);
            throw new Error(error.response?.data?.message || 'Login failed');
        }
    }, []);

    // Logout function
    const logout = useCallback(() => {
        setUser(null);
        localStorage.removeItem('user');
        // Axios interceptor handles the removal of the Authorization header
        // when the 'user' key is no longer found in localStorage.
    }, []);

    const value = {
        user,
        login,
        logout,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook to use the AuthContext
export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === null) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
